import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {Button, Dialog, Input, NavBar, Skeleton, TextArea, Toast} from 'antd-mobile';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from "../../../utils/deviceUtils";


import EmptyState from "../../../components/EmptyState";
import {t} from "../../../i18n";
import pickedUpDataService, {
    PickedUpData,
    PickUpBase64ToFile,
    uploadPhotosWithTimeout
} from "../../../services/PickedUpDataService";
import {fileUpload} from "../../../utils/storageUtils";
import {pickupNavigationService} from '../services/PickupNavigationService';
import pickUpPhotoService, {
    PickUpPhotoType,
    PickUpPositionType,
    PICKUP_FILE_PHOTO_SLOTS
} from "../services/PickUpPhotoService";
import PickUpPhotoViewer from "../components/PickUpPhotoViewer";
import PickUpPhotoUploadCard from "../components/PickUpPhotoUploadCard";
import {PickUpNativePermissions} from "../components/PickUpPhotoNativeBridge";
import PickUpInfoItem from "../components/PickUpInfoItem";
import PickUpBolCard from "../components/PickUpBolCard";
import PickUpBol, {PickUpBolRef, PickUpBolSignatureRole} from "../components/PickUpBol";
import PickUpTrackingNumberScanCard from "../components/PickUpTrackingNumberScanCard";
import {PickupAppointmentType} from "../graphql/types/PickupTaskWithAppointmentsType";
import {buildAddressFromAppointment} from "../../../utils/addressUtils";
import {usePickupTaskAppointment, usePickupTaskOperations} from '../store/pickupStore';
import {getBusinessDaysText, getUnitLabel, isOnsiteContactApplicable} from "../../../enums/PickupEnum";
import {useTranslation} from "react-i18next";
import {PhoneFill} from "antd-mobile-icons";
import dayjs from "dayjs";
import {parseExceptionMessageKey} from "../../../utils/exceptionUtils";
import {formatTimestampByLanguage, formatTodayInTimezone} from "../../../utils/dateTimeUtils";
import {navigateBack} from "../../../components/Map/MapNavigationUtils";
import {usePickupSiteV2Gray} from "../../../hooks/usePickupSiteV2Gray";
import {getUserInfo} from "../../../utils/auth";
import pickUpDraftService from "../services/PickUpDraftService";

interface PickedUpPageProps {}

const MIN_PICKUP_FILE_PHOTOS = 3;

const PickedUpPage: React.FC<PickedUpPageProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { appointmentId, selectedUnit } = useParams<{
        appointmentId: string;
        selectedUnit: string;
    }>();
    // 检查是否来自地图模式
    const fromMapMode = location.state?.fromMapMode || false;
    const { i18n } = useTranslation();
    const [photos, setPhotos] = useState<Record<PickUpPositionType, PickUpPhotoType | undefined>>({} as Record<PickUpPositionType, PickUpPhotoType | undefined>);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pickingUpData, setPickingUpData] = useState<PickupAppointmentType>();
    const [nextItem, setNextItem] = useState<PickupAppointmentType | null>(null);
    const [actualPickupCount, setActualPickupCount] = useState<string>('');
    // 揽收备注：司机现场填写，落到 BOL 的 Notes 栏；与预约单自带的 remark 是两回事
    const [pickupNote, setPickupNote] = useState<string>('');
    // 详细信息次要字段的折叠态，默认收起
    const [detailExpanded, setDetailExpanded] = useState<boolean>(false);
    // 运单号扫描结果，提交时随揽收成功一并上报；与「无法扫描」原因互斥，最多一边有值
    const [trackingNumbers, setTrackingNumbers] = useState<string[]>([]);
    // 其中弱网/无网下跳过校验入列的那些，列表里标红点区分。仅前端展示，不提交后端——
    // 后端靠 order_id 为空 + warn + metric 就能识别未校验记录，再传一个字段是重复建设
    const [unverifiedTrackingNumbers, setUnverifiedTrackingNumbers] = useState<string[]>([]);
    const [unscannableReason, setUnscannableReason] = useState<string | null>(null);
    // 司机位置（开扫时取一次，报「无法扫描」时也取一次），随扫描记录落库佐证司机确实到场
    const [scanPosition, setScanPosition] = useState<{latitude: number; longitude: number} | null>(null);
    // BOL 提货单：浮层显隐 + 双方签字（签字后续随揽收成功提交，故由页面持有）
    const [bolVisible, setBolVisible] = useState<boolean>(false);
    const [bolSignatures, setBolSignatures] = useState<Record<PickUpBolSignatureRole, string | null>>({
        shipper: null,
        driver: null,
    });
    // 签字当时的数量、单位与揽收备注（对应单据的 Qty / Container / Notes 三栏）。
    // 任一与当前不一致，签字即视为无效——双方签的是那张单据的内容，内容变了签字自然不作数。
    // 但不删数据——司机改回原值，签字自动重新生效。
    // 单位不能在本页改（来自路由参数），但退出后重选单位再进来会命中同一份草稿，必须一起校验。
    const [signedQuantity, setSignedQuantity] = useState<string | null>(null);
    const [signedUnit, setSignedUnit] = useState<string | null>(null);
    const [signedRemark, setSignedRemark] = useState<string | null>(null);
    // 单据日期：签字时锁定为签字那天（见 handleBolSave）并随草稿持久化，未签字前显示 HUB 当天。
    // 不能像原先那样 date={dayjs()} 实时取——点「完成」触发重渲染就会跳到第二天，
    // 于是双方签的是 01-01 那张、归档的图却写着 01-02
    const [signedBolDate, setSignedBolDate] = useState<string | null>(null);
    const bolDate = signedBolDate ?? formatTodayInTimezone(pickingUpData?.hubTimezoneLocation);
    const bolSignaturesValid = signedQuantity !== null
        && signedQuantity === actualPickupCount
        && signedUnit === (selectedUnit || '')
        // 比 trim 后的值：单据上 `remark || '-'` 渲染的是可见内容，
        // 末尾多敲一个空格看不出任何差别，不该因此要求重签
        && signedRemark === pickupNote.trim();
    // 传给单据展示的签字：无效期间当作未签，避免单据上出现"旧签字 + 新数量"的组合
    const effectiveBolSignatures = bolSignaturesValid
        ? bolSignatures
        : {shipper: null, driver: null};
    const bolSigned = !!effectiveBolSignatures.shipper && !!effectiveBolSignatures.driver;
    // 已签过字但数量/单位/备注变了：只在 BOL 卡片上常驻提示一处，不再叠 Toast
    const bolSignatureInvalid = !bolSignaturesValid && (!!bolSignatures.shipper || !!bolSignatures.driver);
    // 单据快照在提交时按当前内容现场生成，不再需要"保存出图"这一步，也就不存在图与内容不一致
    const bolRef = useRef<PickUpBolRef>(null);
    // 出好的图暂存于此，供网络失败时随离线队列一起落盘重传
    const capturedBolImageRef = useRef<string | null>(null);
    // 单据上的司机信息取当前登录账号
    const driverAccount = getUserInfo()?.account;
    // BOL 收货方 HUB 随任务查询一并下发，不再单独请求——否则弱网/离线下这三项会是空的
    const consignee = {
        // 优先分拨代码（如 LAX），缺失回落仓库名
        code: pickingUpData?.hubSortingCode || pickingUpData?.hubName || '-',
        address: pickingUpData?.hubAddress || '-',
        contact: pickingUpData?.hubContactName || pickingUpData?.hubContactPhone
            ? `${pickingUpData?.hubContactName || '-'} / ${pickingUpData?.hubContactPhone || '-'}`
            : '-',
    };
    const {pickupTaskAppointment,loading} = usePickupTaskAppointment(appointmentId || '');
    const { updatePickupAppointment, submitPickupOnsiteFeedback } = usePickupTaskOperations();

    // pickup.tracking.scan.gray.switch 灰度：后端按预约单所属 HUB 算好，随任务查询一并下发。
    // 不单独查开关——两个独立请求会出现"任务加载出来了但开关没加载出来"，模块该有却不见。
    // 运单号扫描与 BOL 提货单（含揽收备注，备注是单据 Notes 栏的唯一来源）同属一批交付，共用这一份名单
    const trackingScanGrayEnabled = !!pickingUpData?.trackingScanEnabled;

    // 揽收站点 2.0：揽收成功新增「现场联系人信息」模块（灰度 hub 才展示）
    const { express2Enabled } = usePickupSiteV2Gray();
    // 按 hub 门控：仅当前 appointment 所属 hub 在灰度名单内才启用 2.0（避免"任一 hub 灰度即全量"）
    const siteV2Enabled = express2Enabled(pickingUpData?.warehouseId);
    // 再按站点建联状态门控：只有还在建联流程中（待上门/上门中/揽收成功待建联/揽收失败待建联）的站点才采集，
    // 已建联(4)/确认无法建联(5)/非灰度(null) 不展示。必填校验必须跟着同一个开关走，
    // 否则模块隐藏时"必填"仍在生效，会把提交按钮永久置灰。
    const showOnSiteContact = siteV2Enabled && isOnsiteContactApplicable(pickingUpData?.linkStatus);
    const [onSiteContactName, setOnSiteContactName] = useState<string>('');
    const [onSiteContactPhone, setOnSiteContactPhone] = useState<string>('');
    const [onSiteNote, setOnSiteNote] = useState<string>('');
    const onSitePhoneValid = /^\d{10}$/.test(onSiteContactPhone); // 10 位数字合规校验
    // 模块展示时，联系人 + 10 位联系方式必填；后端契约就绪后随揽收成功一并提交
    const onSiteContactValid = !showOnSiteContact || (onSiteContactName.trim() !== '' && onSitePhoneValid);

    // 草稿回填是否已完成：完成前不允许落盘，否则初始空状态会把已存的草稿冲掉
    const draftRestoredRef = useRef<boolean>(false);

    // 进页面回填草稿（每个 appointmentId 只回填一次）
    useEffect(() => {
        if (!appointmentId || draftRestoredRef.current) {
            return;
        }
        let cancelled = false;
        pickUpDraftService.getDraft(appointmentId, 'pickedUp').then((draft) => {
            if (cancelled) {
                return;
            }
            if (draft) {
                if (draft.photos) {
                    setPhotos(draft.photos);
                }
                // 数量只跟最后一次 BOL 签字走：单位与签字时一致才把签字那一刻的数量恢复出来，
                // 换了单位一律留空重填。签字确立了「按这个单位、这个数量」，别的单位上随手填过的
                // 数字不再有效——否则「托填10签字 → 箱填8签字 → 再选托」会把作废的 10 恢复出来。
                // 代价：填了数量但没签字就返回，回来是空的（哪怕单位没变），这是本口径的必然结果
                if (draft.signedQuantity != null && draft.signedUnit === (selectedUnit || '')) {
                    setActualPickupCount(draft.signedQuantity);
                }
                if (draft.pickupNote !== undefined) {
                    setPickupNote(draft.pickupNote);
                }
                if (draft.trackingNumbers) {
                    setTrackingNumbers(draft.trackingNumbers);
                }
                if (draft.unverifiedTrackingNumbers) {
                    setUnverifiedTrackingNumbers(draft.unverifiedTrackingNumbers);
                }
                if (draft.unscannableReason !== undefined) {
                    setUnscannableReason(draft.unscannableReason);
                }
                // 经纬度成对才有意义，缺一即视为未采集
                if (draft.scanLatitude != null && draft.scanLongitude != null) {
                    setScanPosition({latitude: draft.scanLatitude, longitude: draft.scanLongitude});
                }
                if (draft.bolSignatures) {
                    setBolSignatures(draft.bolSignatures);
                }
                if (draft.signedQuantity !== undefined) {
                    setSignedQuantity(draft.signedQuantity);
                }
                if (draft.signedUnit !== undefined) {
                    setSignedUnit(draft.signedUnit);
                }
                if (draft.signedRemark !== undefined) {
                    setSignedRemark(draft.signedRemark);
                }
                // 老草稿（本字段上线前存的）没有 bolDate，回落为 HUB 当天
                if (draft.bolDate) {
                    setSignedBolDate(draft.bolDate);
                }
                if (draft.onSiteContactName !== undefined) {
                    setOnSiteContactName(draft.onSiteContactName);
                }
                if (draft.onSiteContactPhone !== undefined) {
                    setOnSiteContactPhone(draft.onSiteContactPhone);
                }
                if (draft.onSiteNote !== undefined) {
                    setOnSiteNote(draft.onSiteNote);
                }
            }
            draftRestoredRef.current = true;
        });
        return () => {
            cancelled = true;
        };
    }, [appointmentId, selectedUnit]);

    // 表单任一项变化即落盘；全空时不建草稿，避免只是点进来看一眼也留记录
    useEffect(() => {
        if (!appointmentId || !draftRestoredRef.current) {
            return;
        }
        const hasContent = Object.values(photos).some((photo) => photo?.url)
            || actualPickupCount !== ''
            || pickupNote !== ''
            || trackingNumbers.length > 0
            || unscannableReason !== null
            || !!bolSignatures.shipper
            || !!bolSignatures.driver
            || onSiteContactName !== ''
            || onSiteContactPhone !== ''
            || onSiteNote !== '';
        if (!hasContent) {
            // 表单被清空（例如删掉最后一个运单号）时必须把本地草稿一并删掉：
            // 只 return 不落盘，旧草稿会留在 KVStore 里，退出页面再进来又把已删掉的内容恢复出来
            void pickUpDraftService.removeDraft(appointmentId, 'pickedUp');
            return;
        }
        pickUpDraftService.saveDraft({
            appointmentId,
            type: 'pickedUp',
            updatedAt: Date.now(),
            photos,
            pickupNote,
            trackingNumbers,
            unverifiedTrackingNumbers,
            unscannableReason,
            scanLatitude: scanPosition?.latitude ?? null,
            scanLongitude: scanPosition?.longitude ?? null,
            bolSignatures,
            signedQuantity,
            signedUnit,
            signedRemark,
            bolDate,
            onSiteContactName,
            onSiteContactPhone,
            onSiteNote,
        });
    }, [
        appointmentId,
        selectedUnit,
        photos,
        actualPickupCount,
        pickupNote,
        trackingNumbers,
        unverifiedTrackingNumbers,
        unscannableReason,
        scanPosition,
        bolSignatures,
        signedQuantity,
        signedUnit,
        signedRemark,
        bolDate,
        onSiteContactName,
        onSiteContactPhone,
        onSiteNote,
    ]);

    useEffect(() => {
        if (pickupTaskAppointment) {
            setPickingUpData(pickupTaskAppointment);
        }
    }, [pickupTaskAppointment]);

    // 取下一站会触发列表刷新，不能依赖 pickupTaskAppointment 对象，否则刷新产生新引用又触发本 effect，形成死循环
    useEffect(() => {
        const currentStopNumber = pickingUpData?.sequence;
        if (currentStopNumber && !nextItem) {
            pickupNavigationService.getNextPickupItem(currentStopNumber).then(setNextItem);
        }
    }, [pickingUpData?.sequence]);

    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await PickUpNativePermissions.request('camera');
                await PickUpNativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    // 保存订单数据到本地
    const savePickedUpToLocal = async (): Promise<boolean> => {

        try {
            const pickedUpData: PickedUpData = {
                updatedAt: Date.now(),
                photos: Object.entries(photos || {})
                    .map(([type, photo]) => ({
                    type: type as PickUpPositionType,
                    photo: photo as PickUpPhotoType
                })),
                id: pickingUpData?.id?.toString() || '',
                type: 'pickedUp',
                stopNumber: pickingUpData?.sequence?.toString(),
                channelName: pickingUpData?.channelName,
                actualPickupCount: parseInt(actualPickupCount) || 0,
                driverRemark: pickupNote.trim(),
                actualPickupUnit: parseInt(selectedUnit || '0'),
                taskId: pickingUpData?.taskId,
                cooperationWarehouseId: pickingUpData?.cooperationWarehouseId,
                // 弱网重传时同样要带上，否则运单号/签字/现场联系人会静默丢失
                trackingNumbers,
                unscannableReason,
                scanLatitude: scanPosition?.latitude,
                scanLongitude: scanPosition?.longitude,
                bolSignatures,
                bolImage: capturedBolImageRef.current,
                onSiteContact: {
                    name: onSiteContactName.trim(),
                    phone: onSiteContactPhone,
                    note: onSiteNote.trim(),
                },
                needOnsiteFeedback: siteV2Enabled,
            };

            // 保存到本地
            const saved = await pickedUpDataService.savePickedUpData(pickedUpData);
            return saved;
        } catch (error) {
            console.error('Failed to save PickedUp to local:', error);
            return false;
        }
    };

    /*照片相关操作 start======*/

    // 拍照或从相册选择
    const takePhoto = async (type: PickUpPositionType, file: File) => {
        try {
            setIsLoading(true);
            await pickUpPhotoService.handlePhotoNotUpload(type, file, (updateFn) => {
                setPhotos((prev) => updateFn(prev));
            });
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片
    const deletePhoto = (type: PickUpPositionType) => {
        Dialog.confirm({
            content: t('pages.pickUp.photo.deleteContent'),
            confirmText: t('pages.pickUp.photo.deleteConfirm'),
            cancelText: t('pages.pickUp.photo.deleteCancel'),
            onConfirm: () => {
                pickUpPhotoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    // 运单号列表的唯一写入口：顺带把「未校验」集合裁剪到仍在列表里的单号。
    // 放在这里而不是各删除处，是因为扫描页和卡片都能删，两处各写一遍必然漏
    const handleTrackingNumbersChange = (next: string[]) => {
        setTrackingNumbers(next);
        setUnverifiedTrackingNumbers((prev) => prev.filter((item) => next.includes(item)));
    };

    // 弱网/无网跳过校验入列时由扫描页回调
    const handleMarkUnverified = (trackingNumber: string) => {
        setUnverifiedTrackingNumbers((prev) => (
            prev.includes(trackingNumber) ? prev : [trackingNumber, ...prev]
        ));
    };

    // 浮层点「保存」才提交，此时才以当前数量+单位为新基准。
    // 签完直接返回不会走到这里，基准保持原样，下次带原数量/单位进来旧签字照样有效
    const handleBolSave = (signatures: Record<PickUpBolSignatureRole, string | null>) => {
        setBolSignatures(signatures);
        setSignedQuantity(actualPickupCount);
        setSignedUnit(selectedUnit || '');
        setSignedRemark(pickupNote.trim());
        // 单据日期 = 最后一次签字那天，按 HUB 时区算。绑签字时刻而不是进页面时刻：
        setSignedBolDate(formatTodayInTimezone(pickingUpData?.hubTimezoneLocation));
    };

    // 打开 BOL 提货单：数量要落在单据的货物明细上，未填不允许进入
    const handleOpenBol = () => {
        if (!actualPickupCount) {
            Toast.show({content: t('pages.pickUp.pickedUp.bolNeedActualPickup'), position: 'center'});
            return;
        }
        setBolVisible(true);
    };

    /*照片相关操作 end======*/

    // 揽收成功
    const handleCompletePickedUp = async () => {
        setIsLoading(true);
        try {
            const selectedPhotos = PICKUP_FILE_PHOTO_SLOTS
                .map((slot) => photos[slot])
                .filter((item): item is PickUpPhotoType => item?.objectKey !== undefined && item?.url !== undefined);

            if (selectedPhotos.length < MIN_PICKUP_FILE_PHOTOS) {
                Toast.show({ content: t('pages.pickUp.pickedUp.takeAtLeastPhotos', {count: MIN_PICKUP_FILE_PHOTOS}), position: 'center' });
                return;
            }

            // 先出图再走网络：出图是纯本地操作，放在任何网络调用之后的话，
            // 离线时会因照片上传先抛错而根本执行不到，导致离线队列里没有单据图、重传后 BOL 丢失
            // 灰度未命中时浮层根本没渲染，bolRef.current 为 null，不能进这段
            let bolImageDataUrl: string | null = null;
            if (trackingScanGrayEnabled) {
                try {
                    bolImageDataUrl = await bolRef.current!.captureImage();
                } catch (captureError) {
                    // 出图失败属渲染问题、重试无用，直接提示并中止
                    console.error('Failed to capture BOL snapshot:', captureError);
                    Toast.show({content: t('pages.pickUp.pickedUp.bolCaptureFailed'), position: 'center'});
                    return;
                }
                capturedBolImageRef.current = bolImageDataUrl;
            }

            const currentPhotos = {...photos};
            //仅执行乐观提交逻辑，不进行网络请求
            await updatePickupAppointment({
                appointmentId: pickingUpData?.id ||  '',
                taskId: pickingUpData?.taskId || '0',
                actualParcelCount: parseInt(actualPickupCount) || 0,
                actualParcelUnit: parseInt(selectedUnit || '0'),
                photosS3KeysJson: selectedPhotos.map((photo) => photo.objectKey),
                actualPickupDate: dayjs().format("YYYY-MM-DD"),
                actualPickupTimeEnd: dayjs().format("HH:mm:ss"),
                cooperationWarehouseId: pickingUpData?.cooperationWarehouseId,
            },true);
            setPhotos(currentPhotos);

            // 图片上传，带超时控制
            const photoKeys = await uploadPhotosWithTimeout(selectedPhotos);
            const bolS3Key = bolImageDataUrl
                ? (await fileUpload(await PickUpBase64ToFile(bolImageDataUrl, `bol_${Date.now()}.jpeg`), 'bol')).objectKey
                : undefined;

            //真正的调用接口
            await updatePickupAppointment({
                appointmentId: pickingUpData?.id ||  '',
                taskId: pickingUpData?.taskId || '0',
                actualParcelCount: parseInt(actualPickupCount) || 0,
                actualParcelUnit: parseInt(selectedUnit || '0'),
                photosS3KeysJson: photoKeys,
                actualPickupDate: dayjs().format("YYYY-MM-DD"),
                actualPickupTimeEnd: dayjs().format("HH:mm:ss"),
                cooperationWarehouseId: pickingUpData?.cooperationWarehouseId,
                bolS3Key,
                driverRemark: trackingScanGrayEnabled ? pickupNote.trim() : undefined,
                trackingNumbers,
                unscannableReason: unscannableReason ?? undefined,
                scanLatitude: scanPosition?.latitude,
                scanLongitude: scanPosition?.longitude,
            },false);

            // 揽收站点 2.0 灰度 hub：追加现场回传（补现场联系人 + 推站点建联状态）。
            // best-effort：揽收结果已由上面的 1.0 通路落库，这里失败不回滚、不阻断主流程，仅 toast 提示。
            if (siteV2Enabled && pickingUpData?.id) {
                try {
                    await submitPickupOnsiteFeedback({
                        pickupAppointmentId: pickingUpData.id,
                        pickupSuccess: true,
                        onsiteContactName: onSiteContactName.trim(),
                        onsiteContactPhone: onSiteContactPhone,
                        photosS3KeysJson: JSON.stringify(photoKeys),
                        // 上门备注（→ op5 content.note）
                        note: onSiteNote.trim() || undefined,
                    });
                } catch (feedbackError) {
                    console.error('submitPickupOnsiteFeedback failed (non-blocking):', feedbackError);
                    Toast.show({ content: t('pages.pickUp.onSiteContact.submitFailed'), position: 'center' });
                }
            }

            // 已提交成功，草稿使命结束
            await pickUpDraftService.removeDraftsByAppointment(appointmentId || '');

            if (nextItem && !fromMapMode) {
                navigate(`/pickup/picking-up/detail/${nextItem?.id}/${nextItem?.status}`);
            } else if (fromMapMode) {
                navigateBack.navigateToLsatPage(navigate);
            } else {
                navigateBack.navigateToPickupList(navigate);
            }
        } catch (error: any) {
            if (error.networkError) {
                // 数据已转入重传队列，草稿不再需要（留着会在重传成功后又被回填出来）
                await savePickedUpToLocal();
                await pickUpDraftService.removeDraftsByAppointment(appointmentId || '');
                if (nextItem && !fromMapMode) {
                    navigate(`/pickup/picking-up/detail/${nextItem?.id}/${nextItem?.status}`);
                } else if (fromMapMode) {
                     navigate(-2)
                } else {
                    navigate('/pickup/picking-up');
                }
            } else {
                console.error('Failed to complete picked up:', error);
                const messageKey = parseExceptionMessageKey(error);
                if (messageKey === 'errors.pickup.cancelledAppointmentDotUpdate'){
                    Toast.show({ content: t(messageKey), position: 'center' });
                }else {
                    Toast.show({ content: 'Pickup failed, please try again', position: 'center' });
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCall = () => {
        if (!pickingUpData?.contactPhone || pickingUpData?.contactPhone.trim() === '') {
            Toast.show({
                content: 'No phone number available',
                position: 'bottom',
            });
            return;
        }

        // 清理电话号码，只保留数字和+号
        const cleanPhone = pickingUpData?.contactPhone.replace(/[^\d+]/g, '');

        if (!cleanPhone) {
            Toast.show({
                content: 'Invalid phone number format',
                position: 'bottom',
            });
            return;
        }

        try {
            // 尝试使用原生桥接
            window.location.href = `tel:${cleanPhone}`;
        } catch (error) {
            console.error('Phone call failed:', error);
            Toast.show({
                content: 'Phone call failed, please dial manually: ' + cleanPhone,
                position: 'bottom',
                duration: 3000
            });
        }
    };

    if (loading){
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.pickUp.pickedUp.title')}
                    </NavBar>
                </div>
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={5} animated />
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={3} animated />
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={1} animated />
                <Skeleton style={{width: '100%',height: '30%'}} animated />
            </div>
        );
    }

    if (!pickingUpData) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.pickUp.pickedUp.title')}
                    </NavBar>
                </div>
                <EmptyState description={t(`pages.pickUp.noPickUpInfo`)}/>
            </div>
        );
    }

    const businessDaysStr = getBusinessDaysText(pickingUpData.businessDays, i18n.language);
    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(-1)}>{t('pages.pickUp.pickedUp.title')}</NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* 揽收点信息 */}
                <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.pickedUp.information')}</h2>
                    <div className="mb-1">
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.name')} value={pickingUpData?.channelName} />
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.address')} value={buildAddressFromAppointment(pickingUpData)} />
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.appointmentTime')} value={formatTimestampByLanguage(pickingUpData?.appointmentTime, i18n.language)} />
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.remark')} value={pickingUpData?.remark || '-'} />
                        {/* 次要字段默认折叠，展开后按 站点编号/联系人/电话/预约揽收/营业时间 顺序补全 */}
                        <div
                            className="flex items-center justify-center gap-1 py-2 text-sm"
                            style={{color: 'var(--adm-color-primary)'}}
                            onClick={() => setDetailExpanded((prev) => !prev)}
                        >
                            <span>{t(`pages.pickUp.pickedUp.${detailExpanded ? 'collapseMore' : 'expandMore'}`)}</span>
                            {/* 扁平宽角箭头：用 svg 而非文字符号，避免字形基线偏移和模糊 */}
                            <svg width="13" height="7" viewBox="0 0 13 7" fill="none" className="shrink-0">
                                <path
                                    d={detailExpanded ? 'M1 6l5.5-4.5L12 6' : 'M1 1l5.5 4.5L12 1'}
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        {detailExpanded && (
                            <>
                                <PickUpInfoItem label={t('pages.pickUp.pickedUp.number')} value={pickingUpData?.sequence.toString()} />
                                <PickUpInfoItem label={t('pages.pickUp.pickedUp.contact')} value={pickingUpData?.contactName} />
                                <PickUpInfoItem label={t('pages.pickUp.pickedUp.phone')} value={`${pickingUpData?.contactPhone}${pickingUpData?.ext ? `-${pickingUpData?.ext}` : ''}`} />
                                <PickUpInfoItem label={t('pages.pickUp.pickedUp.appointmentPackages')} value={`${pickingUpData?.quantity} ${getUnitLabel(Number(pickingUpData?.unit), i18n.language)}`} />
                                <PickUpInfoItem label={t('pages.pickUp.pickedUp.businessHours')} value={businessDaysStr}/>
                                <div className="flex justify-between py-2 border-gray-100">
                                    <span className="text-gray-600 text-sm"></span>
                                    <span className="text-sm font-medium text-right">{pickingUpData?.businessHours}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="bg-white rounded-lg p-4 ">
                    {/* 标题、输入框、单位同行展示 */}
                    <div className="flex items-center gap-2">
                        <h2 className="text-base font-semibold whitespace-nowrap shrink-0">{t('pages.pickUp.pickedUp.actualPickup')}:</h2>
                        <Input
                            placeholder={t('pages.pickUp.pickedUp.actualPickupPlaceholder')}
                            value={actualPickupCount}
                            onChange={setActualPickupCount}
                            type={'number'}
                            className="border border-gray-300 rounded-md h-10 px-2 flex-1 [&_.adm-input-element::placeholder]:text-sm"
                        />
                        <h2 className="text-base font-semibold whitespace-nowrap shrink-0">{getUnitLabel(parseInt(selectedUnit || '0'), i18n.language)}</h2>
                    </div>
                    {/* 揽收备注：选填，落到 BOL 的 Notes 栏（不是预约单自带的备注）；跟 BOL 同一份灰度 */}
                    {trackingScanGrayEnabled && (
                    <div className="mt-3.5">
                        <div className="text-sm text-gray-500 mb-1.5">{t('pages.pickUp.pickedUp.pickupNote')}:</div>
                        <TextArea
                            placeholder={t('pages.pickUp.pickedUp.pickupNotePlaceholder')}
                            value={pickupNote}
                            onChange={setPickupNote}
                            maxLength={200}
                            rows={2}
                            className="border border-gray-300 rounded-md px-2 py-1"
                        />
                    </div>
                    )}
                </div>
                {/* BOL 提货单入口（灰度 hub 才展示） */}
                {trackingScanGrayEnabled && (
                <PickUpBolCard
                    signed={bolSigned}
                    note={bolSignatureInvalid ? t('pages.pickUp.pickedUp.bolSignatureInvalidTip') : undefined}
                    onClick={handleOpenBol}
                />
                )}
                {/* 运单号扫描（灰度 hub 才展示） */}
                {trackingScanGrayEnabled && (
                <PickUpTrackingNumberScanCard
                    trackingNumbers={trackingNumbers}
                    unverifiedTrackingNumbers={unverifiedTrackingNumbers}
                    unscannableReason={unscannableReason}
                    onTrackingNumbersChange={handleTrackingNumbersChange}
                    onMarkUnverified={handleMarkUnverified}
                    onUnscannableReasonChange={setUnscannableReason}
                    onLocated={setScanPosition}
                />
                )}
                {/* 现场联系人信息（揽收站点 2.0 · 揽收成功新增模块，灰度 hub + 建联流程中的站点才展示） */}
                {showOnSiteContact && (
                    <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                            <h2 className="text-base font-semibold">{t('pages.pickUp.onSiteContact.title')}</h2>
                            <span className="text-xs text-green-600 bg-green-50 rounded px-1.5 py-0.5">
                                {t('pages.pickUp.onSiteContact.new')}
                            </span>
                        </div>
                        <div className="mb-2">
                            <div className="text-sm mb-1">
                                {t('pages.pickUp.onSiteContact.contactName')} <span className="text-red-500">*</span>
                            </div>
                            <Input
                                placeholder={t('pages.pickUp.onSiteContact.contactNamePlaceholder')}
                                value={onSiteContactName}
                                onChange={setOnSiteContactName}
                                className="border border-gray-300 rounded-md h-10 px-2"
                            />
                        </div>
                        <div>
                            <div className="text-sm mb-1">
                                {t('pages.pickUp.onSiteContact.contactPhone')} <span className="text-red-500">*</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 border border-gray-300 rounded-md h-10 flex items-center px-2">+1</span>
                                <Input
                                    placeholder={t('pages.pickUp.onSiteContact.contactPhonePlaceholder')}
                                    value={onSiteContactPhone}
                                    onChange={(v) => setOnSiteContactPhone(v.replace(/\D/g, '').slice(0, 10))}
                                    type="tel"
                                    className="border border-gray-300 rounded-md h-10 px-2 flex-1"
                                />
                            </div>
                            {onSiteContactPhone !== '' && !onSitePhoneValid && (
                                <div className="text-xs text-red-500 mt-1">{t('pages.pickUp.onSiteContact.phoneInvalid')}</div>
                            )}
                        </div>
                        <div className="mt-2">
                            <div className="text-sm mb-1">{t('pages.pickUp.onSiteContact.note')}</div>
                            <TextArea
                                placeholder={t('pages.pickUp.onSiteContact.notePlaceholder')}
                                value={onSiteNote}
                                onChange={setOnSiteNote}
                                maxLength={200}
                                rows={2}
                                className="border border-gray-300 rounded-md px-2 py-1"
                            />
                        </div>
                    </div>
                )}
                {/*上传图片*/}
                <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.pickedUp.photos')}</h2>
                    <p className="text-sm text-gray-600 mb-1">
                        {t('pages.pickUp.pickedUp.takeAtLeastPhotos', {count: MIN_PICKUP_FILE_PHOTOS})}
                    </p>
                    <div className="grid grid-cols-3 gap-2 my-4">
                        {PICKUP_FILE_PHOTO_SLOTS.map((slot) => (
                            <PickUpPhotoUploadCard
                                key={slot}
                                photoUrl={photos[slot]?.url}
                                type={slot}
                                description={``}
                                onTakePhoto={takePhoto}
                                onDelete={deletePhoto}
                                onView={handleViewPhoto}
                            />
                        ))}
                    </div>
                </div>

                {/* 查看大图 */}
                <PickUpPhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
            </div>

            {/* 提交按钮 */}
            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <div className="flex-0">
                    <Button
                        shape="rounded"
                        className="w-12 h-12 flex items-center justify-center"
                        onClick={handleCall}
                        style={{'--background-color': '#f0f0f0'}}
                    >
                        <PhoneFill className="text-xl"/>
                    </Button>
                </div>
                <Button
                    block
                    color="primary"
                    loading={isLoading}
                    disabled={PICKUP_FILE_PHOTO_SLOTS.filter((slot) => photos[slot]?.url).length < MIN_PICKUP_FILE_PHOTOS || isLoading || !actualPickupCount || !onSiteContactValid || (trackingScanGrayEnabled && ((trackingNumbers.length === 0 && unscannableReason === null) || !bolSigned))}
                    onClick={handleCompletePickedUp}
                >
                    {t('pages.pickUp.pickedUp.complete')}
                </Button>
            </div>

            {/* BOL 提货单全屏浮层（灰度 hub 才挂载；不挂载时 bolRef 为空，提交处已同步门控） */}
            {trackingScanGrayEnabled && (
            <PickUpBol
                visible={bolVisible}
                onClose={() => setBolVisible(false)}
                bolNumber={pickingUpData?.appointmentNumber ? `BOL${pickingUpData.appointmentNumber}` : '-'}
                date={bolDate}
                pickup={{
                    // 站点ID 取揽收站点（合作仓）；warehouseId 是收货方 HUB，不是提货方
                    code: pickingUpData?.cooperationWarehouseId || '-',
                    address: buildAddressFromAppointment(pickingUpData),
                    contact: `${pickingUpData?.contactName || '-'} / ${pickingUpData?.contactPhone || '-'}`,
                }}
                consignee={consignee}
                container={getUnitLabel(parseInt(selectedUnit || '0'), i18n.language)}
                quantity={actualPickupCount || '-'}
                driverName={driverAccount?.name || '-'}
                driverPhone={driverAccount?.phone || '-'}
                licensePlate={pickingUpData?.licensePlate || '-'}
                remark={pickupNote}
                shipperSignature={effectiveBolSignatures.shipper}
                driverSignature={effectiveBolSignatures.driver}
                ref={bolRef}
                onSave={handleBolSave}
            />
            )}
        </div>
    );
};

export default PickedUpPage;
